import type { AccountSave } from './account';
import type { CharacterSave } from './character';
import { type AccSlot, type EquipItem, type EquipSlot, type GearItem, ITEMS, itemDef } from './items';

/**
 * Inventory operations. Stackable items live in `stacks`, unique equipment in
 * `items`; each distinct stack and each unique item uses one slot.
 */

export function slotsUsed(c: CharacterSave): number {
  return Object.values(c.inventory.stacks).filter((n) => n > 0).length + c.inventory.items.length;
}

export function freeSlots(c: CharacterSave): number {
  return c.inventory.capacity - slotsUsed(c);
}

export function countOf(c: CharacterSave, id: string): number {
  return c.inventory.stacks[id] ?? 0;
}

/** Adds stackable items; returns the amount actually added (inventory space permitting). */
export function addStack(c: CharacterSave, id: string, n: number): number {
  if (n <= 0) return 0;
  const def = itemDef(id);
  const have = c.inventory.stacks[id] ?? 0;
  if (have === 0 && freeSlots(c) <= 0) return 0;
  const room = def.stack - have;
  const add = Math.min(room, n);
  c.inventory.stacks[id] = have + add;
  return add;
}

export function removeStack(c: CharacterSave, id: string, n: number): boolean {
  const have = c.inventory.stacks[id] ?? 0;
  if (have < n) return false;
  const left = have - n;
  if (left <= 0) delete c.inventory.stacks[id];
  else c.inventory.stacks[id] = left;
  return true;
}

export function addItem(c: CharacterSave, item: EquipItem): boolean {
  if (freeSlots(c) <= 0) return false;
  c.inventory.items.push(item);
  return true;
}

export function removeItem(c: CharacterSave, uid: string): EquipItem | null {
  const i = c.inventory.items.findIndex((it) => it.uid === uid);
  if (i < 0) return null;
  return c.inventory.items.splice(i, 1)[0] ?? null;
}

/** Which equipment slot an item goes into (accessories pick the first free matching slot). */
export function slotFor(c: CharacterSave, item: EquipItem): EquipSlot {
  if (item.kind === 'gear') return item.slot;
  if (item.kind === 'stone') return 'stone';
  const pair: Record<string, AccSlot[]> = { necklace: ['necklace'], earring: ['earring1', 'earring2'], ring: ['ring1', 'ring2'] };
  const options = pair[item.slot] ?? ['necklace'];
  return options.find((s) => !c.equipment[s]) ?? (options[0] as AccSlot);
}

/** Equips an inventory item, swapping out whatever occupied the slot. */
export function equip(c: CharacterSave, uid: string, slot?: EquipSlot): boolean {
  const item = c.inventory.items.find((it) => it.uid === uid);
  if (!item) return false;
  const target = slot ?? slotFor(c, item);
  const eq = c.equipment as Record<string, EquipItem | undefined>;
  const old = eq[target];
  removeItem(c, uid);
  eq[target] = item;
  if (old) c.inventory.items.push(old);
  return true;
}

export function unequip(c: CharacterSave, slot: EquipSlot): boolean {
  const eq = c.equipment as Record<string, EquipItem | undefined>;
  const item = eq[slot];
  if (!item || freeSlots(c) <= 0) return false;
  delete eq[slot];
  c.inventory.items.push(item);
  return true;
}

/** Sell value of a unique item. */
export function itemValue(item: EquipItem): number {
  const r = ['normal', 'uncommon', 'rare', 'epic', 'legendary', 'relic', 'ancient', 'mythic'].indexOf(item.rarity);
  if (item.kind === 'gear') return Math.round((20 + item.baseIlvl * 2) * (1 + r * 0.6));
  if (item.kind === 'accessory') return Math.round((30 + item.ilvl * 2) * (1 + r * 0.8));
  return 50 * (1 + r);
}

export function sellItem(c: CharacterSave, a: AccountSave, uid: string): number {
  const item = removeItem(c, uid);
  if (!item) return 0;
  const v = itemValue(item);
  a.wallet.silver += v;
  return v;
}

export function sellStack(c: CharacterSave, a: AccountSave, id: string, n: number): number {
  const def = ITEMS[id];
  if (!def || !removeStack(c, id, n)) return 0;
  const v = def.value * n;
  a.wallet.silver += v;
  return v;
}

/** Salvage (dismantle) gear into Aether Shards. */
export function salvage(c: CharacterSave, uid: string): number {
  const item = removeItem(c, uid);
  if (!item) return 0;
  const shards = item.kind === 'gear' ? Math.max(10, Math.round(item.baseIlvl * 0.8)) : 25;
  addStack(c, 'aether_shard', shards);
  return shards;
}

export interface Cost {
  silver?: number;
  gold?: number;
  items?: Record<string, number>;
}

export function canAfford(c: CharacterSave, a: AccountSave, cost: Cost): boolean {
  if ((cost.silver ?? 0) > a.wallet.silver) return false;
  if ((cost.gold ?? 0) > a.wallet.gold) return false;
  for (const [id, n] of Object.entries(cost.items ?? {})) if (countOf(c, id) < n) return false;
  return true;
}

export function pay(c: CharacterSave, a: AccountSave, cost: Cost): boolean {
  if (!canAfford(c, a, cost)) return false;
  a.wallet.silver -= cost.silver ?? 0;
  a.wallet.gold -= cost.gold ?? 0;
  for (const [id, n] of Object.entries(cost.items ?? {})) if (n > 0) removeStack(c, id, n);
  return true;
}

/** Best equipped-or-better check used by loot popups. */
export function isUpgrade(c: CharacterSave, g: GearItem): boolean {
  const cur = c.equipment[g.slot];
  if (!cur) return true;
  return g.baseIlvl + g.plus * 5 > cur.baseIlvl + cur.plus * 5;
}
